import React from 'react';
import './WizardSteps.css';

/**
 * WizardSteps — multi-step progress indicator for wizards.
 *
 * Props:
 * @param {Array<{key: string, label: string}>} steps - Ordered list of steps.
 * @param {string} [currentKey] - Key of the currently active step. Takes priority over currentIndex.
 * @param {number} [currentIndex] - Zero-based index of the currently active step (fallback if currentKey not provided).
 *
 * Step states:
 * - Completed (index < current): orange filled background + checkmark icon
 * - Active (index === current): orange border + orange number
 * - Future (index > current): gray border + gray number
 *
 * Connector lines between steps reflect completion state:
 * - Orange when the preceding step is completed
 * - Gray when the preceding step is future/active
 *
 * @example
 * <WizardSteps
 *   steps={[
 *     { key: 'placement', label: '選擇版位' },
 *     { key: 'basic', label: '基本資訊' },
 *     { key: 'targeting', label: 'Targeting' },
 *   ]}
 *   currentKey="basic"
 * />
 */
function WizardSteps({ steps = [], currentKey, currentIndex, onStepClick, className }) {
  // Resolve the active index
  let activeIndex;
  if (currentKey !== undefined) {
    activeIndex = steps.findIndex((s) => s.key === currentKey);
    if (activeIndex === -1) activeIndex = 0;
  } else if (currentIndex !== undefined) {
    activeIndex = currentIndex;
  } else {
    activeIndex = 0;
  }

  return (
    <nav className={`ds-wizard-steps${className ? ` ${className}` : ''}`} aria-label="步驟進度">
      {steps.map((step, idx) => {
        const isDone = idx < activeIndex;
        const isActive = idx === activeIndex;
        const stepState = isDone ? 'done' : isActive ? 'active' : 'future';
        const isClickable = onStepClick && (isDone || isActive);

        return (
          <React.Fragment key={step.key ?? idx}>
            {/* Connector line before each step (except the first) */}
            {idx > 0 && (
              <div
                className={`ds-wizard-steps__line${idx <= activeIndex ? ' ds-wizard-steps__line--done' : ''}`}
                aria-hidden="true"
              />
            )}

            <div
              className={`ds-wizard-steps__step ds-wizard-steps__step--${stepState}`}
              aria-current={isActive ? 'step' : undefined}
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
              style={isClickable ? { cursor: 'pointer' } : undefined}
              onClick={isClickable ? () => onStepClick(idx) : undefined}
              onKeyDown={isClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') onStepClick(idx); } : undefined}
            >
              <div className="ds-wizard-steps__bubble" aria-hidden="true">
                {isDone ? (
                  <svg
                    className="ds-wizard-steps__check"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M2.5 7.5L5.5 10.5L11.5 4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <span>{idx + 1}</span>
                )}
              </div>
              <span className="ds-wizard-steps__label">{step.label}</span>
            </div>
          </React.Fragment>
        );
      })}
    </nav>
  );
}

export default WizardSteps;
